import { BidderRule } from '@pubstack/common/src/bidder-rule'
import { cn } from '@pubstack/ui/utils/cn'
import { FunctionComponent, useMemo } from 'react'
import { useBidderSeats } from '~/api/bidder-seats.api'
import Button from '~/components/Button'
import { Icon } from '~/components/Icon'
import { Flyout } from '~/components/Flyout'
import { BidderMediaTypeList } from '~/feature-components/BidderMediaTypeList'
import { Spinner } from '~/components/Spinner'
import { BidderRuleSectionHeading } from './BidderRuleSectionHeading'
import { BIDDER_RULE_FORM_SECTIONS, bySeatName, formSectionOfBidderSeat, sectionAvailability, storedSectionOf } from './bidderRuleSections'
import { useBidderRuleCapabilities } from './useBidderRuleCapabilities'

type Props = {
  bidderRule: BidderRule
  /** Offered by the stack form, which lets a publisher jump from the rule it picked to editing it. */
  onEdit?: () => void
  isEditable?: boolean
}

export const ViewBidderRuleFlyout: FunctionComponent<Props> = ({ bidderRule, onEdit, isEditable }) => {
  const { data: admSeats, isLoading: isLoadingAdm } = useBidderSeats('adm')
  const { data: pbsSeats, isLoading: isLoadingPbs } = useBidderSeats('pbs')
  const capabilities = useBidderRuleCapabilities()

  const seatsById = useMemo(() => Object.fromEntries([...admSeats, ...pbsSeats].map((seat) => [seat.id, seat])), [admSeats, pbsSeats])

  // Grouped by section so the flyout reads like the editor. A selected seat whose id no longer
  // resolves is skipped rather than rendered blank — a rule can outlive a deleted seat.
  //
  // Sorted by name, like the editor's own sections: `selectedBidderSeats` is stored in the order seats
  // were ticked, so an unsorted list put the same bidders in a different place in the summary than in the
  // form they were read from, and gave no order at all to look a bidder up by.
  const selectedBySection = useMemo(() => {
    return BIDDER_RULE_FORM_SECTIONS.map((section) => ({
      section,
      entries: bidderRule.selectedBidderSeats
        .flatMap((entry) => {
          const seat = seatsById[entry.id]
          return seat && formSectionOfBidderSeat(seat) === section ? [{ seat, mediaTypes: entry.mediaTypes }] : []
        })
        .sort(bySeatName),
    }))
  }, [bidderRule.selectedBidderSeats, seatsById])

  // How much is riding on this rule, as two numbers. `usedInStacks` is keyed by media name, so the medias
  // are its keys and the stacks are what they hold. The two used to be listed row by row underneath; the
  // count is what a publisher about to edit a rule is asking for, and the names answered a different
  // question at the cost of most of the flyout.
  const stackCount = useMemo(() => Object.values(bidderRule.usedInStacks ?? {}).reduce((total, stacks) => total + stacks.length, 0), [bidderRule.usedInStacks])
  const mediaCount = Object.keys(bidderRule.usedInStacks ?? {}).length

  return (
    <Flyout.Content>
      <Flyout.Title>
        <span className="flex items-center gap-2">
          {bidderRule.isDefault && (
            <span role="img" aria-label="Default rule" className="flex">
              <Icon name="star" width="18px" height="18px" fill={'var(--color-king)'} />
            </span>
          )}
          {bidderRule.name} - details
        </span>
      </Flyout.Title>
      <Flyout.Body>
        {isLoadingAdm || isLoadingPbs ? (
          <Spinner />
        ) : (
          <div className="flex flex-col gap-6">
            {selectedBySection.map(({ section, entries }) => {
              const isBlocked = sectionAvailability(section, capabilities) === 'blocked'
              return (
                <section key={section} className="flex flex-col gap-2">
                  {/* Auto-add describes what happens when a seat arrives. A section this account cannot
                      reach never gets one, so the badge would answer a question that never comes up —
                      the padlock answers the one that does, and matches the editor's. */}
                  <BidderRuleSectionHeading section={section} isBlocked={isBlocked}>
                    {!isBlocked && (
                      /* Stated either way rather than only when on: absence of a badge reads as "this
                         view does not mention auto-add", not as "auto-add is off". */
                      <span
                        className={cn('rounded px-2 py-0.5 text-xs', bidderRule.autoAddNewBidderSeats[storedSectionOf(section)] ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500')}
                      >
                        {bidderRule.autoAddNewBidderSeats[storedSectionOf(section)] ? 'Auto-add on' : 'Auto-add off'}
                      </span>
                    )}
                  </BidderRuleSectionHeading>
                  {/* An empty blocked section is not an empty one: "No bidder enabled" invites the reader
                      to go and enable one, which is the single thing they cannot do here. */}
                  <BidderMediaTypeList
                    rows={entries.map(({ seat, mediaTypes }) => ({ key: seat.id, label: seat.name, mediaTypes }))}
                    emptyLabel={isBlocked ? 'Not available on this account.' : 'No bidder enabled.'}
                  />
                </section>
              )
            })}

            {/* Rendered even when empty — "used by nothing" is the one answer a publisher about to edit a
                rule most wants, and hiding the section withholds it. */}
            <section className="flex flex-col gap-2">
              <h3 className="font-medium">
                Used by {mediaCount} media{mediaCount === 1 ? '' : 's'}
                {stackCount > 0 && (
                  <span className="font-normal text-slate-500">
                    {' '}
                    · {stackCount} stack{stackCount === 1 ? '' : 's'}
                  </span>
                )}
              </h3>
              {stackCount === 0 && <p className="text-sm text-slate-500">This rule is not used by any stack.</p>}
            </section>
          </div>
        )}
      </Flyout.Body>
      {/* In the footer, where every view flyout keeps its way out of reading and into editing — it used to
          sit at the top of the body, which put it above the thing it edits and scrolled away with it. */}
      {onEdit && isEditable && (
        <Flyout.Actions>
          <Button variant="secondary" iconName="edit" onClick={onEdit} trackingLabel="common-bidder-rules-edit">
            Edit rule
          </Button>
        </Flyout.Actions>
      )}
    </Flyout.Content>
  )
}
